import Vue from 'vue'
import Vuex from 'vuex'
import fa from "element-ui/src/locale/lang/fa";

Vue.use(Vuex)

// Tabs 持久化：读取 sessionStorage
function readPersistedTabs() {
    try {
        const tabsStr = sessionStorage.getItem('editableTabs')
        const activeStr = sessionStorage.getItem('editableTabsValue')
        const tabs = tabsStr ? JSON.parse(tabsStr) : null
        const active = activeStr ? JSON.parse(activeStr) : null
        if (Array.isArray(tabs) && tabs.length > 0) {
            return { tabs, active }
        }
    } catch (e) {}
    return {
        tabs: [{ title: '首页', name: 'Index', path: '/index' }],
        active: 'Index'
    }
}

function persistTabs(tabs, active) {
    try {
        if (tabs) sessionStorage.setItem('editableTabs', JSON.stringify(tabs))
        if (active !== undefined) sessionStorage.setItem('editableTabsValue', JSON.stringify(active))
    } catch (e) {}
}

/**
 * 递归提取菜单树中的所有权限标识（permission字段）
 * @param {Array} menus 菜单数组
 * @returns {Array} 权限标识数组
 */
function extractPermissions(menus) {
    const permissions = []
    
    if (!Array.isArray(menus) || menus.length === 0) {
        return permissions
    }
    
    const traverse = (nodes) => {
        if (!Array.isArray(nodes)) return
        
        nodes.forEach(node => {
            // 如果节点有permission字段且不为空，添加到权限列表
            if (node.permission && node.permission.trim()) {
                permissions.push(node.permission.trim())
            }
            
            // 递归处理子节点
            if (node.children && Array.isArray(node.children) && node.children.length > 0) {
                traverse(node.children)
            }
        })
    }
    
    traverse(menus)
    
    // 去重并返回
    return [...new Set(permissions)]
}

export default {
    state: {
        menuList: [],
        permList: [],
        hasRoute: false,

        // 全局菜单加载状态与单例 Promise，避免重复请求
        menusLoaded: false,
        _loadMenusPromise: null,

        // 头部Logo版本号，用于触发重新加载
        logoVersion: 0,

        editableTabsValue: readPersistedTabs().active,
        editableTabs: readPersistedTabs().tabs,
    },
    mutations: {
        setMenuList(state, menus) {
            state.menuList = menus;
            state.menusLoaded = Array.isArray(menus) && menus.length > 0;
        },
        setPermList(state, perms) {
            state.permList = perms;
        },
        changeRouteStatus(state, hasRoute) {
            state.hasRoute = hasRoute;
            sessionStorage.setItem("hasRoute", hasRoute);
        },
        _setLoadMenusPromise(state, promise) {
            state._loadMenusPromise = promise;
        },
        incrementLogoVersion(state) {
            state.logoVersion = (state.logoVersion || 0) + 1;
        },
        // 新增：设置激活tab
        setActiveTab(state, name) {
            if (!name) return
            state.editableTabsValue = name
            persistTabs(undefined, state.editableTabsValue)
        },
        // 新增：插入或更新并激活tab
        upsertTab(state, tab) {
            if (!tab || !tab.name) return
            const idx = state.editableTabs.findIndex(e => e.name === tab.name)
            if (idx === -1) {
                // 缺省标题容错
                const title = tab.title !== undefined ? tab.title : tab.name
                state.editableTabs.push({
                    title: title,
                    name: tab.name,
                    path: tab.path
                })
            } else {
                // 更新标题或路径（以最新为准）
                const existing = state.editableTabs[idx]
                const next = {
                    title: tab.title !== undefined ? tab.title : (existing.title !== undefined ? existing.title : tab.name),
                    name: tab.name,
                    path: tab.path !== undefined ? tab.path : existing.path
                }
                state.editableTabs.splice(idx, 1, next)
            }
            state.editableTabsValue = tab.name
            persistTabs(state.editableTabs, state.editableTabsValue)
        },
        // 新增：根据name移除tab（保留首页）
        removeTabByName(state, name) {
            if (!name || name === 'Index') return
            const idx = state.editableTabs.findIndex(e => e.name === name)
            if (idx !== -1) {
                state.editableTabs.splice(idx, 1)
                // 修复问题1：移除标签页后立即更新持久化存储
                persistTabs(state.editableTabs, state.editableTabsValue)
            }
        },
        addTab(state, tab) {
            let index = state.editableTabs.findIndex(e => e.name === tab.name);
            if (index === -1 && tab.title !== undefined) {
                state.editableTabsValue = tab.name;
                state.editableTabs.push({
                    title: tab.title,
                    name: tab.name,
                    path:tab.path
                });
                persistTabs(state.editableTabs, state.editableTabsValue)
            }
        },
        resetState: (state) => {
            state.token = '';
            state.menuList = [];
            state.permList = [];
            state.hasRoute = false;
            state.menusLoaded = false;
            state._loadMenusPromise = null;
            state.logoVersion = 0;
            state.editableTabsValue = 'Index';
            state.editableTabs = [{
                title: '首页',
                name: 'Index',
                path: '/index'
            }];
            persistTabs(state.editableTabs, state.editableTabsValue)

        }

    },
    actions: {
        // 统一加载当前用户菜单，返回菜单数组
        // allowForceRefresh: 是否强制重新加载菜单，即使已经加载过
        async loadMenusOnce({ state, commit }, { allowForceRefresh = false } = {}) {
            // 已有进行中的请求且不强制刷新，直接复用
            if (state._loadMenusPromise && !allowForceRefresh) {
                return state._loadMenusPromise;
            }

            // 如果已加载过且不强制刷新，直接返回现有数据
            if (state.menusLoaded && state.menuList && state.menuList.length > 0 && !allowForceRefresh) {
                return Promise.resolve(state.menuList);
            }

            // 如果强制刷新，先重置菜单状态
            if (allowForceRefresh) {
                commit('setMenuList', []);
                state.menusLoaded = false;
                commit('_setLoadMenusPromise', null);
            }

            // 动态导入以避免循环依赖
            const { get } = await import('@/api/api');

            const promise = get('/homepage/getCurrentMenuTree')
                .then(res => {
                    // 如果是 11006 等错误码，会自动跳转登录页，响应数据已在 axios 拦截器中统一处理
                    const menuList = res.data && res.data.data ? res.data.data : [];
                    commit('setMenuList', menuList);
                    
                    // 提取所有权限标识并存储
                    const permissions = extractPermissions(menuList);
                    commit('setPermList', permissions);
                    
                    return menuList;
                })
                .catch(error => {
                    // 处理请求错误（如网络错误等）
                    console.error('加载菜单失败:', error);
                    throw error;
                })
                .finally(() => {
                    // 请求结束后释放单例
                    commit('_setLoadMenusPromise', null);
                });

            commit('_setLoadMenusPromise', promise);
            return promise;
        }
    },

}
